import { useMutation, useQuery } from '@apollo/client';
import { Col, notification, Radio, Row } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import AddressFormatComponent from 'components/common/AddressFormatComponent';
import { InputComponent } from 'components/common/Input';
import { LabelStyled } from 'components/common/Input/controlled/styled';
import { INPUT_TYPE, LABEL_TYPE } from 'components/common/Input/types';
import SweetAlertAsModal from 'components/common/SweetAlertAsModal';
import InputFieldsGenerator from 'components/InputFieldsGenerator';
import { DataFieldProps } from 'components/InputFieldsGenerator/types';
import { FORMAT_DATE_TIME_US_FOR_DISPLAY } from 'constants/days';
import { Form, Formik } from 'formik';
import useFormikRefHook from 'hooks/useFormikRefHook';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import ROUTES from 'routes/routeConstants';
import { UPDATE_MANY_APPOINTMENT_ASSIGNMENT } from 'services/graphql/mutations/appointmentAssignments';
import { UPDATE_USER } from 'services/graphql/mutations/users';
import { GET_REQUIRED_SCAN_ASSIGNMENT } from 'services/graphql/queries/appointmentAssignment.query';
import { GET_SINGLE_USER } from 'services/graphql/queries/user.query';
import { AssignmentStatus, IAssignment } from 'types/appointmentAssignments';
import { IUserAddress } from 'types/user';
import { defaultNotification, formatAddress } from 'utils';
import IntlMessages from 'utils/IntlMessages';
import * as Yup from 'yup';

import { addressFields, addressValidationObject } from './Payment';
import { MailingAddressOption } from './Payment/types';
import PortalProfileSaveButton from './PortalProfileSaveButton';
import { FormActionButtonStyled } from './styled';
import { ITabProps } from './types';

const DocumentShipping: React.FC<ITabProps> = ({
  user,
  hideSaveButton,
  onSubmit,
  submitButtonLabel,
  portalButtonClassName,
  onSubmitCallback,
  showNotificationOnSuccess = true,
  showNotificationOnFailure = true,
}) => {
  const [scanRequiredAppointmentWarning, setScanRequiredAppointmentWarning] = useState(false);
  const [documentSettingsState, setDocumentSettings] = useState<any>({});
  const isSetupWizard = !!useRouteMatch({
    exact: true,
    path: ROUTES.USER_WIZARD,
  })?.isExact;
  const containerClass =
    portalButtonClassName || (isSetupWizard ? '.steps-content' : '.custom-tabs-content-active .gx-tab-content-header');
  // const wrapperQuery = isSetupWizard ? '.steps-content .active' : '.custom-tabs-content-active';

  // user main address
  const { email } = user;
  const fullAddress = formatAddress(user);
  const userShippingAddress = user.docShippingAddress;
  // user doc shipping address
  const shippingAddress = userShippingAddress ? formatAddress(userShippingAddress) : '';

  const defaultOption =
    !shippingAddress || shippingAddress === fullAddress ? MailingAddressOption.PRIMARY : MailingAddressOption.NEW;
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const ref = useFormikRefHook('shipping');
  const baseInitFields = {
    ableScanDoc: user.ableScanDoc || false,
  };

  const withNewAddressInitFields = {
    ...baseInitFields,
    address1: userShippingAddress?.address1 || '',
    address2: userShippingAddress?.address2 || '',
    city: userShippingAddress?.city || '',
    zip: userShippingAddress?.zip || '',
    state: userShippingAddress?.state || '',
  };

  const baseValidationObject = useMemo(() => ({}), []);
  const newAddressValidationObject = useMemo(() => ({ ...baseValidationObject, ...addressValidationObject }), [
    selectedOption,
  ]);

  const [updateUser, { loading }] = useMutation(UPDATE_USER, {
    refetchQueries: [
      {
        query: GET_SINGLE_USER,
        variables: {
          email,
        },
      },
    ],
  });

  const handleChooseMailingAddress = (event: RadioChangeEvent) => {
    setSelectedOption(event.target.value);
  };

  const updateUserPayment = async ({
    docShippingAddress,
    ableScanDoc,
  }: {
    docShippingAddress: IUserAddress;
    ableScanDoc: boolean;
  }) => {
    try {
      await updateUser({
        variables: {
          filter: {
            id: user.id,
          },
          updatedDoc: {
            docShippingAddress,
            ableScanDoc,
          },
        },
      });

      console.log('updated documentShipping');

      // UserAction.fetchUserSuccess({
      //   docShippingAddress,
      // });
      if (showNotificationOnSuccess)
        notification.success({
          message: 'Update profile successfully!',
          key: 'updateProfile',
          duration: 2,
        });
    } catch (error) {
      if (showNotificationOnFailure)
        notification.error({
          message: 'Update payment failed!',
          key: 'updateProfile',
          duration: 2,
        });
    }
  };

  const getAddressToUpdate = (formDto: IUserAddress, option: MailingAddressOption): IUserAddress => {
    if (option === MailingAddressOption.NEW) {
      return {
        address1: formDto.address1,
        address2: formDto.address2,
        city: formDto.city,
        state: formDto.state,
        zip: formDto.zip,
      };
    }
    return {
      address1: user.address1,
      address2: user.address2,
      city: user.city,
      state: user.state,
      zip: user.zip,
    };
  };

  const { data } = useQuery<{
    findRequiredScanDocumentAssignmentEntity: IAssignment[];
  }>(GET_REQUIRED_SCAN_ASSIGNMENT);
  const requiredScanAssignments = data?.findRequiredScanDocumentAssignmentEntity;

  const [updateManyAssignment] = useMutation(UPDATE_MANY_APPOINTMENT_ASSIGNMENT);

  const callSubmitFunction = async ({ ableScanDoc, ...value }: any, cancelAppointment?: boolean) => {
    if (onSubmit) {
      onSubmit({ value, ableScanDoc });
      return;
    }
    const paymentAddressDetail: IUserAddress = getAddressToUpdate(value, selectedOption);
    await updateUserPayment({ docShippingAddress: paymentAddressDetail, ableScanDoc });
    if (!cancelAppointment) {
      onSubmitCallback?.(value);
      setScanRequiredAppointmentWarning(false);
      return;
    }
    try {
      await updateManyAssignment({
        variables: {
          filter: requiredScanAssignments?.map((as) => ({ id: as.id })),
          updatedDoc: requiredScanAssignments?.map(() => ({ status: AssignmentStatus.REJECTED })),
        },
      });
      defaultNotification({ success: 'All required-scan appointments cancelled!' });
    } catch (err) {
      defaultNotification({ failure: 'Cancelling required-scan appointments failed! Please try again' });
    } finally {
      setScanRequiredAppointmentWarning(false);
    }
  };

  const onSubmitPayment = ({ ableScanDoc, ...value }: any) => {
    if (!ableScanDoc && requiredScanAssignments?.length && user.ableScanDoc) {
      setDocumentSettings({ ableScanDoc, ...value });
      setScanRequiredAppointmentWarning(true);
      return;
    }
    callSubmitFunction({ ableScanDoc, ...value });
  };

  const primaryAddressData: DataFieldProps[] = [
    {
      label: 'Address',
      value: <AddressFormatComponent addressDetail={user} separateLines />,
    },
  ];

  return (
    <div className='gx-signin-form gx-form-row0 document-shipping-address'>
      <SweetAlertAsModal
        show={scanRequiredAppointmentWarning && !!requiredScanAssignments?.length}
        info
        showCancel
        confirmBtnText='Keep Appointment'
        cancelBtnText='Cancel Appointment'
        title='Update Digitally Required Appointments'
        onConfirm={() => {
          callSubmitFunction(documentSettingsState);
        }}
        onCancel={() => {
          callSubmitFunction(documentSettingsState, true);
        }}
        focusConfirmBtn>
        <div className='text-justify'>
          <div className='mt-4 mb-2'>
            The following appointments require for documents to be scanned and digitally returned:
          </div>
          {requiredScanAssignments?.map((as) => {
            const requiredScanAppointment = as.appointment;
            return (
              <div className='font-medium' key={as.id}>
                Appointment {requiredScanAppointment?.description ? `${requiredScanAppointment.description} - ` : ''}
                {moment(requiredScanAppointment.date).format(FORMAT_DATE_TIME_US_FOR_DISPLAY)}
              </div>
            );
          })}
          <div className='mt-2'>Can you scan and return documents for these already scheduled appointments?</div>
        </div>
      </SweetAlertAsModal>
      <Formik
        initialValues={selectedOption === MailingAddressOption.PRIMARY ? baseInitFields : withNewAddressInitFields}
        onSubmit={onSubmitPayment}
        validationSchema={
          selectedOption === MailingAddressOption.PRIMARY
            ? Yup.object(baseValidationObject)
            : Yup.object(newAddressValidationObject)
        }
        innerRef={ref}
        validateOnBlur>
        {(props) => (
          <Form className='gx-signin-form gx-form-row0 gx-payment-form'>
            <div className='pl-4 mb-2'>
              <InputComponent
                type={INPUT_TYPE.SWITCH}
                name='ableScanDoc'
                label='Own a scanner and can return documents digitally'
                labelType={LABEL_TYPE.right}
              />
            </div>
            <Row>
              <Col span={24}>
                <LabelStyled $labelType={LABEL_TYPE.above}>
                  <div>Where should mailed documents for you be sent?</div>
                </LabelStyled>
                <Row className='gx-payment-row-field'>
                  <Col span={24} className='gx-px-0'>
                    <Radio.Group
                      className='gx-payment-mailing-option gx-mb-3'
                      onChange={handleChooseMailingAddress}
                      value={selectedOption}>
                      <Row>
                        <Col span={10} md={12} className='gx-px-0'>
                          <Radio className='option-button' value={MailingAddressOption.PRIMARY}>
                            Use Primary Address
                          </Radio>
                        </Col>
                        <Col span={14} md={12} className='gx-pr-0'>
                          <Radio className='option-button' value={MailingAddressOption.NEW}>
                            Specify Payment Mailing Address
                          </Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Col>
                  {selectedOption === MailingAddressOption.PRIMARY ? (
                    <div style={{ margin: '0 -16px' }} className='gx-w-100'>
                      <InputFieldsGenerator dataFields={primaryAddressData} />
                    </div>
                  ) : (
                    <Col style={{ margin: '0 -16px' }} className='side-field option-child' span={24}>
                      <InputFieldsGenerator formFields={addressFields} />
                    </Col>
                  )}
                </Row>
              </Col>
            </Row>
            <Row justify='end' className={hideSaveButton ? 'gx-d-none' : ''}>
              <Col span={24}>
                <Row justify='start'>
                  <PortalProfileSaveButton containerClass={containerClass}>
                    <FormActionButtonStyled
                      isLoading={loading}
                      buttonType='primary'
                      htmlType='button'
                      className={`gx-mb-0 gx-mr-0 ${hideSaveButton ? 'gx-d-none' : ''}`}
                      onClick={() => {
                        props.handleSubmit();
                      }}>
                      {submitButtonLabel || <IntlMessages id='userProfile.saveProfile' />}
                    </FormActionButtonStyled>
                  </PortalProfileSaveButton>
                </Row>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default React.memo(DocumentShipping);
